import { useState, useCallback, useMemo } from 'react';

import omit from 'lodash/omit';
import map from 'lodash/map';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';

import {
  UniversalFilterField,
  UniversalFilterFieldName
} from '../../UniversalFilter.types';
import { ID, FetchItemsFilterItems } from '../../../../../types';

import { useUniversalFilterClearFieldsSavedState } from '../useUniversalFilterClearFieldsSavedState';

interface UniversalFilterLocalStateOptions<FiltersType> {
  viewId: ID;
  filterFields: UniversalFilterField<FiltersType>[];
  currentFilters?: FiltersType;
  onFilter: FetchItemsFilterItems<Partial<FiltersType>>;
  afterClear?: () => void;
  afterApply?: () => void;
}

function useUniversalFilterLocalState<FiltersType extends Record<string, any>>({
  viewId,
  filterFields,
  currentFilters,
  onFilter,
  afterClear,
  afterApply
}: UniversalFilterLocalStateOptions<FiltersType>) {
  const filterFieldNames = useMemo<UniversalFilterFieldName<FiltersType>[]>(
    () => map(filterFields, ({ fieldName }) => fieldName),
    [filterFields]
  );

  const currentFieldsFilters = pick<FiltersType>(
    currentFilters,
    filterFieldNames
  );

  const isFiltered = !isEmpty(currentFieldsFilters);

  const [localFiltersState, setLocalFiltersState] =
    useState<Partial<FiltersType>>(currentFieldsFilters);

  const handleApply = useCallback<() => void>(() => {
    onFilter({
      ...omit(currentFilters, filterFieldNames),
      ...localFiltersState
    });
    afterApply?.();
  }, [
    localFiltersState,
    currentFilters,
    filterFieldNames,
    afterApply,
    onFilter
  ]);

  const { handleUniversalFilterClearFieldsSavedState } =
    useUniversalFilterClearFieldsSavedState();

  const handleClear = useCallback<() => void>(() => {
    const fieldNames = map(filterFields, ({ fieldName }) => fieldName);
    onFilter(omit<FiltersType>(currentFilters, fieldNames));
    setLocalFiltersState({});
    handleUniversalFilterClearFieldsSavedState({ viewId, fieldNames });
    afterClear?.();
  }, [
    filterFields,
    currentFilters,
    afterClear,
    viewId,
    handleUniversalFilterClearFieldsSavedState,
    onFilter
  ]);

  return {
    isFiltered,
    localFiltersState,
    setLocalFiltersState,
    handleApply,
    handleClear
  };
}

export default useUniversalFilterLocalState;
